import React, { useEffect, useState } from "react";
import DashboardLayout from "../../../layout/DashboardLayout";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress,
} from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import { getAgencies } from "../../../api/auth";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import GroupsIcon from "@mui/icons-material/Groups";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { brand } from "../../../theme";
import { EmptyState } from "../../../components/EmptyState";

const TIERS = [
  {
    key: 0,
    label: "Tier 0",
    sublabel: "No KYC submitted",
    color: "#94a3b8",
    Icon: PendingActionsIcon,
  },
  {
    key: 1,
    label: "Tier 1",
    sublabel: "KYC Submitted",
    color: brand.terracotta,
    Icon: AssignmentTurnedInIcon,
  },
  {
    key: 2,
    label: "Tier 2",
    sublabel: "PCC Approved",
    color: "#f59e0b",
    Icon: FlightTakeoffIcon,
  },
  {
    key: 3,
    label: "Tier 3",
    sublabel: "Contract Signed",
    color: "#10b981",
    Icon: WorkspacePremiumIcon,
  },
];

const STAT_CARDS = [
  {
    label: "Total Agencies",
    sublabel: "All registered",
    color: "#6366f1",
    Icon: GroupsIcon,
  },
  ...TIERS.filter((t) => t.key > 0),
];

const getAgencyTier = (a) => {
  if (a.contractStatus === "Signed") return 3;
  if (a.pccStatus === "Approved") return 2;
  if (a.kycStatus === "Submitted") return 1;
  return 0;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box
      sx={{
        bgcolor: "white",
        p: 1.5,
        border: "1px solid #e2e8f0",
        borderRadius: 1.5,
        boxShadow: 3,
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ color: d.color }}>
        {d.label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {d.sublabel}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ color: d.color, mt: 0.5 }}
      >
        {d.value} {d.value === 1 ? "agency" : "agencies"} · {d.pct}%
      </Typography>
    </Box>
  );
};

export default function InternalAdminDashboard() {
  const { user, loading } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getAgencies();
        setAgencies(res.data || []);
      } catch (err) {
        console.error("Error fetching agencies:", err);
        setError(
          "Failed to load agencies. Please refresh the page and try again.",
        );
      } finally {
        setFetching(false);
      }
    })();
  }, []);

  if (loading || fetching) {
    return (
      <DashboardLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </DashboardLayout>
    );
  }

  const total = agencies.length;
  const tierCounts = TIERS.map((t) => {
    const count = agencies.filter((a) => getAgencyTier(a) === t.key).length;
    return {
      ...t,
      value: count,
      pct: total ? Math.round((count / total) * 100) : 0,
    };
  });
  const pieData = tierCounts.filter((t) => t.value > 0);

  const passedKyc =
    tierCounts[1].value + tierCounts[2].value + tierCounts[3].value;
  const pccApproved = tierCounts[2].value + tierCounts[3].value;
  const contracted = tierCounts[3].value;
  const conversions = [
    {
      label: "KYC Rate",
      value: total ? Math.round((passedKyc / total) * 100) : 0,
      hint: "submitted KYC",
    },
    {
      label: "PCC Rate",
      value: passedKyc ? Math.round((pccApproved / passedKyc) * 100) : 0,
      hint: "KYC → PCC approved",
    },
    {
      label: "Contract Rate",
      value: pccApproved ? Math.round((contracted / pccApproved) * 100) : 0,
      hint: "PCC → contract signed",
    },
  ];

  const statValues = [
    total,
    tierCounts[1].value,
    tierCounts[2].value,
    tierCounts[3].value,
  ];

  return (
    <DashboardLayout>
      {/* Header */}
      <Box
        mb={4}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: "#0f172a" }}>
            Overview
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            Welcome back, {user?.name || "Admin"}, here's your agency pipeline.
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "#94a3b8", alignSelf: "center" }}
        >
          {new Date().toLocaleDateString("en-GB", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </Typography>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2.5} mb={4}>
        {STAT_CARDS.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card
              sx={{
                borderLeft: `5px solid ${card.color}`,
                background: `linear-gradient(135deg, #ffffff 0%, ${card.color}12 100%)`,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                height: "100%",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.09)",
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 2.5,
                }}
              >
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      color: "#94a3b8",
                      fontWeight: 600,
                      fontSize: "0.65rem",
                      letterSpacing: 1,
                    }}
                  >
                    {card.label}
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight={800}
                    sx={{ color: card.color, lineHeight: 1.1, mt: 0.25 }}
                  >
                    {statValues[i]}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    {card.sublabel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: `${card.color}18`,
                    borderRadius: "14px",
                    width: 52,
                    height: 52,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <card.Icon sx={{ color: card.color, fontSize: "1.6rem" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3}>
        {/* Donut Chart */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{ height: "100%", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "#0f172a" }}
              >
                Tier Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Current stage breakdown across all agencies
              </Typography>

              {total === 0 ? (
                <Box display="flex" alignItems="center" justifyContent="center" minHeight={240}>
                  <EmptyState
                    variant="compact"
                    icon={<GroupsIcon sx={{ fontSize: "1.5rem", color: "#cbd5e1" }} />}
                    description="No agency data yet."
                  />
                </Box>
              ) : (
                <>
                  <Box sx={{ position: "relative" }}>
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={68}
                          outerRadius={108}
                          paddingAngle={3}
                          dataKey="value"
                          stroke="none"
                        >
                          {pieData.map((entry) => (
                            <Cell key={entry.key} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <Typography
                        variant="h3"
                        fontWeight={800}
                        sx={{ color: "#0f172a", lineHeight: 1 }}
                      >
                        {total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                    mt={1.5}
                    justifyContent="center"
                  >
                    {tierCounts.map((t) => (
                      <Box
                        key={t.key}
                        display="flex"
                        alignItems="center"
                        gap={0.75}
                      >
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: "50%",
                            bgcolor: t.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {t.label}{" "}
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, color: "#0f172a" }}
                          >
                            {t.value}
                          </Box>
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pipeline */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{ height: "100%", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "#0f172a" }}
              >
                Onboarding Pipeline
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Stage-by-stage distribution and funnel conversion
              </Typography>

              <Box display="flex" flexDirection="column" gap={2.5}>
                {tierCounts.map((t) => (
                  <Box key={t.key}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Box
                          sx={{
                            bgcolor: `${t.color}18`,
                            borderRadius: "8px",
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <t.Icon sx={{ color: t.color, fontSize: "1.1rem" }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: "#0f172a", lineHeight: 1.3 }}
                          >
                            {t.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t.sublabel}
                          </Typography>
                        </Box>
                      </Box>
                      <Box textAlign="right">
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          sx={{ color: t.color, lineHeight: 1.2 }}
                        >
                          {t.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.pct}%
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={t.pct}
                      sx={{
                        height: 7,
                        borderRadius: 4,
                        bgcolor: `${t.color}18`,
                        "& .MuiLinearProgress-bar": {
                          bgcolor: t.color,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Funnel Conversion */}
              <Box mt={3} pt={2.5} sx={{ borderTop: "1px solid #f1f5f9" }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: "#94a3b8",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    letterSpacing: 1,
                  }}
                >
                  Funnel Conversion
                </Typography>
                <Grid container spacing={1.5} mt={0.25}>
                  {conversions.map((c) => (
                    <Grid item xs={4} key={c.label}>
                      <Box
                        sx={{
                          bgcolor: "#f8fafc",
                          borderRadius: 2,
                          p: 1.5,
                          textAlign: "center",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <Typography
                          variant="h5"
                          fontWeight={800}
                          sx={{ color: "#0f172a", lineHeight: 1 }}
                        >
                          {c.value}%
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="text.secondary"
                          display="block"
                          mt={0.25}
                        >
                          {c.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          {c.hint}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
}
